import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

export type DailyTrendData = {
  date: string;
  value: number;
};

type AttendanceBarChartProps = {
  data: DailyTrendData[];
  height?: number;
  isLoading?: boolean;
};

const MARGIN = { top: 20, right: 20, bottom: 40, left: 40 };

export default function AttendanceBarChart({ data, height = 240, isLoading = false }: AttendanceBarChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [tooltip, setTooltip] = useState<{ x: number, y: number, date: string, value: number, visible: boolean }>({
    x: 0, y: 0, date: '', value: 0, visible: false
  });

  useEffect(() => {
    const obs = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w) setWidth(Math.max(w, 300));
    });
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  const draw = () => {
    if (!svgRef.current || data.length === 0) return;

    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = height - MARGIN.top - MARGIN.bottom;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('width', width).attr('height', height);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    // Scales
    const x = d3.scaleBand()
      .domain(data.map(d => d.date))
      .range([0, innerW])
      .padding(0.3);

    const y = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.value) || 10])
      .nice()
      .range([innerH, 0]);

    // Grid lines
    g.append('g')
      .attr('class', 'grid')
      .attr('opacity', 0.05)
      .call(d3.axisLeft(y).tickSize(-innerW).tickFormat(() => ''));

    // Axes
    const xAxis = d3.axisBottom(x)
      .tickValues(x.domain().filter((_, i) => i % 5 === 0))
      .tickFormat(d => {
        const date = new Date(d);
        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      });

    g.append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(xAxis)
      .call(g => g.select('.domain').attr('stroke', 'rgba(0,0,0,0.1)'))
      .call(g => g.selectAll('.tick line').attr('stroke', 'rgba(0,0,0,0.1)'))
      .call(g => g.selectAll('text')
        .attr('fill', 'rgba(0,0,0,0.4)')
        .attr('font-size', '9px')
        .attr('font-weight', '700')
        .attr('font-family', 'inherit')
        .attr('dy', '1.5em'));

    const yAxis = d3.axisLeft(y).ticks(5);
    g.append('g')
      .call(yAxis)
      .call(g => g.select('.domain').remove())
      .call(g => g.selectAll('.tick line').attr('stroke', 'rgba(0,0,0,0.1)'))
      .call(g => g.selectAll('text')
        .attr('fill', 'rgba(0,0,0,0.4)')
        .attr('font-size', '9px')
        .attr('font-weight', '700')
        .attr('font-family', 'inherit'));

    // Bars
    g.selectAll('.ab-bar')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'ab-bar')
      .attr('x', d => x(d.date) || 0)
      .attr('y', innerH)
      .attr('width', x.bandwidth())
      .attr('height', 0)
      .attr('fill', isLoading ? '#d4d4d8' : '#AAD816')
      .attr('rx', 2)
      .attr('opacity', isLoading ? 0.3 : 0.8)
      .on('mouseenter', (event, d) => {
        if (isLoading) return;
        const [mx, my] = d3.pointer(event, svgRef.current);
        setTooltip({
          x: mx,
          y: my,
          date: new Date(d.date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }),
          value: d.value,
          visible: true
        });
        d3.select(event.currentTarget).transition().duration(200).attr('opacity', 1).attr('fill', '#000');
      })
      .on('mouseleave', (event) => {
        setTooltip(prev => ({ ...prev, visible: false }));
        d3.select(event.currentTarget).transition().duration(200).attr('opacity', 0.8).attr('fill', '#AAD816');
      })
      .transition()
      .duration(800)
      .delay((_d, i) => i * 20)
      .attr('y', d => y(d.value))
      .attr('height', d => innerH - y(d.value));
  };

  useEffect(() => {
    draw();
  }, [data, width, isLoading]);

  return (
    <div ref={containerRef} className="relative w-full select-none">
      <svg ref={svgRef} className="w-full overflow-visible" />
      
      {tooltip.visible && (
        <div 
          className="absolute z-50 pointer-events-none"
          style={{ left: tooltip.x, top: tooltip.y - 60, transform: 'translateX(-50%)' }}
        >
          <div className="bg-white border border-zinc-200 rounded-xl px-3 py-2 shadow-2xl text-center backdrop-blur-md">
            <div className="text-zinc-400 text-[9px] font-bold uppercase tracking-widest leading-none mb-1">{tooltip.date}</div>
            <div className="text-zinc-950 font-black text-sm tabular-nums">{tooltip.value} check-ins</div>
          </div>
          {/* Arrow */}
          <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-white border-r border-b border-zinc-200 rotate-45" />
        </div>
      )}
    </div>
  );
}
